<script setup>
    import { ref, reactive, onMounted, watch, onErrorCaptured } from 'vue';
    import Bugsnag from '@bugsnag/js';
    import BugsnagPluginVue from '@bugsnag/plugin-vue';
    import BoundaryError from './BoundaryError.vue';

    const isProd = process.env.APP_ENV === 'production';
    const error = ref(false);
    const message = ref("");
    const component = ref("");
    const trace = ref("");
    const bugsnagEnabled = ref(!!process.env.MIX_BUGSNAG_API_KEY);
    const ready = ref(false);
    const logs = reactive(new Set());

    const reportError = (error) => {
        if (bugsnagEnabled.value && ready.value) {
            return Bugsnag.notify(error, undefined, () => {
                console.log('Bugsnag notified');
            });
        } else if (bugsnagEnabled.value && !ready.value) {
            return logs.add(error);
        }
    };

    onErrorCaptured((captured, vm) => {
        const { message: msg, stack } = captured;
        error.value = true;
        message.value = msg;
        trace.value = stack;

        const componentNameMatch = stack.match(/resources\/js\/(.*?).vue/g);
        if (componentNameMatch && componentNameMatch[0]) {
            const componentPath = String(componentNameMatch[0]).split('/');
            component.value = componentPath[componentPath.length - 1] ?? componentNameMatch[0];
        } else {
            component.value = (vm.$.type.name || vm.$.type.__file || 'unknown') ?? null;
        }
        console.error(captured);
        const errorObj = new Error(`${msg} in ${component.value}`);
        errorObj.stack = stack;
        reportError(errorObj);

        return false;
    });

    onMounted(() => {
        console.log('%c[Boundary] Loaded!', 'color: skyblue;');
        if (bugsnagEnabled.value) {
            Bugsnag.start({
                appVersion: '1.0.0',
                apiKey: process.env.MIX_BUGSNAG_API_KEY,
                plugins: [new BugsnagPluginVue()],
                releaseStage: process.env.MIX_BUGSNAG_API_KEY,
            });

            ready.value = Bugsnag.isStarted();
        }

        const onRequestFailure = (event) => {
            reportError(event.detail);
        };

        document.addEventListener('xhrerror', onRequestFailure);
        document.addEventListener('fetcherror', onRequestFailure);
        document.addEventListener('fetchabort', onRequestFailure);

        const originalOpen = XMLHttpRequest.prototype.open;
        XMLHttpRequest.prototype.open = function (_, url) {
            this.addEventListener('load', function () {
                document.dispatchEvent(new Event('xhrload'));
            });
            originalOpen.apply(this, arguments);
        };

        const originalFetch = fetch;
        fetch = function (url, options) {
            return originalFetch(url, options).catch((error) => {
                error.message += ` (while fetching ${url})`;
                document.dispatchEvent(
                    new CustomEvent('fetchabort', {
                        detail: error,
                    })
                );

                throw error;
            });
        };
    });

    watch(
        () => ready.value,
        () => {
            if (ready.value) {
                [...logs.values()].forEach((log, idx) => {
                    if (log?.detail) {
                        reportError(new Error(`\n ${log.detail.stack} \n in Component: ${log.detail.component}`));
                    } else if (log instanceof Error) {
                        reportError(log);
                    }

                    if (idx === logs.size - 1) logs.clear();
                });
            }
        }
    );
</script>

<template>
    <BoundaryError
        v-if="error"
        :title="isProd ? 'We ran into a snag, please try again later.' : `Something went wrong in component: ${component}`"
        :message="isProd ? '' : message"
        :stacktrace="isProd ? '' : trace"
    />

    <div v-else>
        <slot></slot>
    </div>
</template>
